<template>
  <AppLayout title="Kundendaten bearbeiten">
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div class="flex justify-between items-center">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">{{ $t('Kundendaten bearbeiten') }}</h1>
              <p class="text-sm text-gray-600 mt-1">{{ $t('Aktualisieren Sie Ihre Geschäftsinformationen') }}</p>
            </div>
            <Link 
              :href="route('customerAccount')" 
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {{ $t('Zurück') }}
            </Link>
          </div>
        </div>
      </div>

      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Success Message -->
        <div v-if="successMessage" class="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-green-700">{{ successMessage }}</p>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="Object.keys(errors).length > 0" class="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">{{ $t('Es gab Fehler bei Ihrer Eingabe') }}</h3>
              <div class="mt-2 text-sm text-red-700">
                <ul class="list-disc pl-5 space-y-1">
                  <li v-for="error in errors" :key="error">{{ error }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Form -->
        <form @submit.prevent="saveChanges" class="bg-white shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('Geschäftsinformationen') }}</h2>
          </div>
          
          <div class="p-6 space-y-6">
            <!-- Customer Name -->
            <div>
              <label for="name" class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('Firmenname') }}
                <span class="text-red-500">*</span>
              </label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                :class="{ 'border-red-300': errors.name }"
              />
              <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
              <p class="mt-1 text-sm text-gray-500">{{ $t('Der offizielle Name Ihres Unternehmens') }}</p>
            </div>

            <!-- VAT ID -->
            <div>
              <label for="vat_id" class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('USt-IdNr.') }}
              </label>
              <div class="mt-1 relative">
                <input
                  id="vat_id"
                  v-model="form.vat_id"
                  type="text"
                  :placeholder="$t('z.B. DE123456789')"
                  class="block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                  :class="{ 'border-red-300': errors.vat_id }"
                />
                <div v-if="isValidatingVat" class="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <svg class="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              </div>
              <p v-if="errors.vat_id" class="mt-1 text-sm text-red-600">{{ errors.vat_id }}</p>
              <p v-else-if="vatValidationResult" :class="['mt-1 text-sm', vatValidationResult.valid ? 'text-green-600' : 'text-orange-600']">
                {{ vatValidationResult.message }}
              </p>
              <p class="mt-1 text-sm text-gray-500">{{ $t('Ihre Umsatzsteuer-Identifikationsnummer für steuerfreie B2B-Transaktionen') }}</p>
            </div>

            <!-- Invoice Email -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t('Rechnungs-E-Mail') }}
                <span class="text-red-500">*</span>
              </label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 sm:text-sm"
                :class="{ 'border-red-300': errors.email }"
              />
              <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
              <p class="mt-1 text-sm text-gray-500">{{ $t('An diese E-Mail-Adresse werden alle Rechnungen gesendet') }}</p>
            </div>

            <!-- Additional Information (Read-only) -->
            <div class="pt-6 border-t border-gray-200 space-y-4">
              <h3 class="text-sm font-semibold text-gray-900">{{ $t('Weitere Informationen') }}</h3>
              
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-500">{{ $t('Kundennummer') }}</label>
                  <p class="mt-1 text-sm text-gray-900">{{ customer.customer_number }}</p>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-500">{{ $t('Land') }}</label>
                  <p class="mt-1 text-sm text-gray-900">{{ getCountryName(customer.country) }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <Link
              :href="route('customerAccount')"
              class="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
            >
              {{ $t('Abbrechen') }}
            </Link>
            <button
              type="submit"
              :disabled="isProcessing || isValidatingVat"
              class="px-4 py-2 bg-sonnenglas-600 text-white text-sm font-medium rounded-md hover:bg-sonnenglas-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isProcessing || isValidatingVat" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isValidatingVat ? $t('USt-IdNr. wird validiert...') : $t('Speichern...') }}
              </span>
              <span v-else>{{ $t('Änderungen speichern') }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, reactive, watch, inject } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import AppLayout from '@/layouts/AppLayout.vue'
import debounce from 'lodash/debounce'

const route = inject<(name?: string, params?: any, absolute?: boolean) => string>('route')

interface Customer {
  id: number
  name: string
  customer_number: string
  country: string
  vat_id: string | null
  email: string
}

interface Country {
  code: string
  name: string
}

interface Props {
  customer: Customer
  countries: Country[]
}

const props = defineProps<Props>()

const form = reactive({
  name: props.customer.name,
  vat_id: props.customer.vat_id || '',
  email: props.customer.email
})

const errors = ref<Record<string, string>>({})
const isProcessing = ref(false)
const isValidatingVat = ref(false)
const vatValidationResult = ref<{ valid: boolean; message: string } | null>(null)
const successMessage = ref('')

const getCountryName = (countryCode: string) => {
  const country = props.countries.find(c => c.code === countryCode)
  return country ? country.name : countryCode
}

// VAT validation with debounce - calls backend VIES API
const validateVat = debounce(async (vatId: string) => {
  if (!vatId || vatId.length < 4) {
    vatValidationResult.value = null
    return
  }

  // Check basic format first
  const vatPattern = /^[A-Z]{2}[0-9A-Z]+$/
  if (!vatPattern.test(vatId.toUpperCase())) {
    vatValidationResult.value = {
      valid: false,
      message: trans('Format ungültig. Beispiel: DE123456789')
    }
    return
  }

  isValidatingVat.value = true
  try {
    if (!route) {
      console.error('Route helper not available')
      return
    }

    // Call our API endpoint which validates via backend VIES
    const response = await fetch(route('api.vat.validate'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ vat_id: vatId.toUpperCase() })
    })

    if (response.ok) {
      const data = await response.json()
      vatValidationResult.value = {
        valid: data.valid,
        message: data.message || ''
      }
    } else {
      vatValidationResult.value = {
        valid: false,
        message: trans('Validierung fehlgeschlagen')
      }
    }
  } catch (error) {
    console.error('VAT validation error:', error)
    vatValidationResult.value = {
      valid: false,
      message: trans('Validierung fehlgeschlagen')
    }
  } finally {
    isValidatingVat.value = false
  }
}, 800)

watch(() => form.vat_id, (newValue) => {
  if (newValue) {
    validateVat(newValue.toUpperCase())
  } else {
    vatValidationResult.value = null
  }
})

const saveChanges = () => {
  if (!route) {
    console.error('Route helper not available')
    return
  }

  errors.value = {}
  successMessage.value = ''

  // Validate
  if (!form.name.trim()) {
    errors.value.name = trans('Firmenname ist erforderlich')
  }

  if (!form.email.trim()) {
    errors.value.email = trans('E-Mail-Adresse ist erforderlich')
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.value.email = trans('Ungültige E-Mail-Adresse')
  }

  // Prevent submission if VAT validation is still in progress
  if (isValidatingVat.value) {
    errors.value.vat_id = trans('Bitte warten Sie auf die Validierung der USt-IdNr.')
    return
  }

  // Prevent submission if VAT validation failed
  if (form.vat_id && vatValidationResult.value && !vatValidationResult.value.valid) {
    errors.value.vat_id = vatValidationResult.value.message || trans('USt-IdNr. ist ungültig')
    return
  }

  if (Object.keys(errors.value).length > 0) {
    return
  }

  isProcessing.value = true

  router.post(route('customer.update'), {
    name: form.name.trim(),
    vat_id: form.vat_id ? form.vat_id.toUpperCase().trim() : null,
    email: form.email.trim()
  }, {
    preserveState: true,
    preserveScroll: true,
    onSuccess: () => {
      successMessage.value = trans('Ihre Daten wurden erfolgreich aktualisiert.')
      setTimeout(() => {
        successMessage.value = ''
      }, 5000)
    },
    onError: (errors) => {
      if (errors.name) errors.value.name = errors.name
      if (errors.vat_id) errors.value.vat_id = errors.vat_id  
      if (errors.email) errors.value.email = errors.email
    },
    onFinish: () => {
      isProcessing.value = false
    }
  })
}
</script>